import {
	InvestableUniverseSelectionStrategy,
	InvestableUniverseType,
	type InvestmentCreationConfigurationControllerV4Api,
	type InvestmentDraftConfigurationControllerV4Api,
	type InvestmentEnhancementConfigurationControllerV4Api,
	type PartialInvestableUniverseTicker,
	type UserInstrumentDto,
} from "@eu.mdotm.api/sphere";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	Banner,
	CircularProgressBar,
	Controller,
	colorBySeverity,
	Dialog,
	DialogFooter,
	DialogHeader,
	Icon,
	Searchable,
	Text,
} from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, adaptAnimatedNodeProvider, renderNodeOrFn, spawn } from "@mdotm/shadcn/legacy/react-extensions";
import { builtInSortFnFor, unpromisify } from "@mdotm/shadcn/legacy/utils";
import { Radio, RadioGroup, Select, TextInput, Button, FormField, SubmitButton } from "@mdotm/shadcn/mdotm/components";
import { unreachable } from "@mdotm/utils/runtime";
import { useQuery } from "@tanstack/react-query";
import type { ColDef } from "ag-grid-community";
import type * as Immutable from "immutable";
import { useMemo } from "react";
import { FormProvider, type UseFormReturn, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { reportPlatformError } from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { IconWalls } from "$root/components/IconWall";
import { useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { instrumentsRepository } from "$root/functional-areas/instruments/repository";
import { useDebouncedNameUniquenessChecker } from "$root/functional-areas/named-entities/uniqueness";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { universeWithGroupsRepository } from "$root/functional-areas/universe/universeWithGroupsRepository";
import { useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { ManualCreationEntitiesEnum } from "$root/pages/ManualCreation/common";
import { UniverseWithGroupsCardContent } from "$root/pages/UniverseDetails/partials/UniverseWithGroupsCardContent";
import { AgGridWithModules } from "$root/third-party-integrations/ag-grid/ag-grid";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { FormController } from "$root/third-party-integrations/react-hook-form";
import { useMappedQuery, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { validateZodDefinition } from "$root/third-party-integrations/zod";
import { FormFields } from "$root/ui-lib/form/FormFields";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { sphereColumnsDef } from "$root/ui-lib/table/column/platform-columns-definition";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { assocStatusToEntity, type InvestableUniverseInstrument } from "../constraints/const";
import type { EditPortfolioV4Props } from "../EditPortfolio.shared-types";
import { useStepSync } from "../hooks";
import { responseToStepMapper } from "../requests";
import { StepBase } from "../StepBase";
import {
	type EditPortfolioV4StepPayloadMap,
	type InvestableUniverseStep,
	type StepPropsFor,
	useHandleSubmitToCustomSubmitHandlerInContext,
} from "../shared";

const CURRENT_COMPOSITION_ID = "CREATE_FROM_CURRENT_COMPOSITION";
const setValueOptions = {
	shouldDirty: true,
	shouldTouch: true,
	shouldValidate: false,
};
export function getInvestableUniverseStepData(
	createApi: Omit<InvestmentCreationConfigurationControllerV4Api, "basePath" | "axios">,
	enhanceApi: Omit<InvestmentEnhancementConfigurationControllerV4Api, "basePath" | "axios">,
	draftApi: Omit<InvestmentDraftConfigurationControllerV4Api, "basePath" | "axios">,
	area: EditPortfolioV4Props["area"],
): Promise<EditPortfolioV4StepPayloadMap["investableUniverse"]> {
	return (
		!area.portfolioUid
			? createApi.getCreationConfigurationInvestableUniverse()
			: area.name === "draft"
				? draftApi.getDraftConfigurationInvestableUniverse(area.portfolioUid)
				: enhanceApi.getEnhancementConfigurationInvestableUniverse(area.portfolioUid)
	).then(({ data }) => responseToStepMapper.investableUniverse(data, area));
}

export function Step02_InvestableUniverse(props: StepPropsFor<"investableUniverse">): React.JSX.Element {
	const { context, stepMetadata, stepData, toggleDirty, onStepDataChange, onStepError } = props;
	const { area, saveHandlers } = context;

	const { push } = useTypedNavigation();
	const initialData = useMemo(
		() =>
			({
				...stepData,
				// A group universe never goes through the merge-strategy dialog, so nothing would set a
				// selection strategy and the discriminated union of every non-create area would reject the
				// step. "create" is excluded on purpose: its schema requires an undefined strategy.
				investableUniverseSelectionStrategy:
					area.name !== "create" && stepData.type === "GROUP_UNIVERSE"
						? "SELL_INSTRUMENTS"
						: stepData.investableUniverseSelectionStrategy,
			}) satisfies typeof stepData,
		[stepData, area.name],
	);

	const form = useForm({
		defaultValues: initialData,
		resolver: zodResolver(
			area.name === "create"
				? z
						.object({
							investableUniverseSelectionStrategy: z.undefined(),
						} satisfies Partial<Record<keyof typeof stepData, unknown>>)
						.and(commonInvestableUniverseValidation)
				: z
						.discriminatedUnion("investableUniverseSelectionStrategy", [
							z
								.object({
									investableUniverseSelectionStrategy: z.literal(InvestableUniverseSelectionStrategy.CreateNew),
								})
								.passthrough(),
							z
								.object({
									investableUniverseSelectionStrategy: z.literal(InvestableUniverseSelectionStrategy.Expand),
								})
								.passthrough(),
							z
								.object({
									investableUniverseSelectionStrategy: z.literal(InvestableUniverseSelectionStrategy.SellInstruments),
								})
								.passthrough(),
						])
						.superRefine((item, ctx) => {
							switch (item.investableUniverseSelectionStrategy) {
								case "EXPAND":
								case "SELL_INSTRUMENTS":
									validateZodDefinition(commonInvestableUniverseValidation)(item, ctx);
									break;
								case "CREATE_NEW":
									validateZodDefinition(z.object(investableUniverseValidationObject))(item, ctx);
									break;
								default:
									unreachable(item);
							}
						}),
		),
	});

	const mode = useMemo(() => (area.editable && area.edit ? "edit" : "read"), [area.edit, area.editable]);
	const { reset, formState, watch, handleSubmit, getValues, setValue } = form;
	const universeIdentifier = watch("universeIdentifier");
	const initialUniverseIdentifier = getValues("universeIdentifier");
	const initialUniverseType = getValues("type");
	useStepSync({
		reset,
		toggleDirty,
		isDirty: formState.isDirty,
		stepData: initialData,
	});
	const onSubmitAsync = useHandleSubmitToCustomSubmitHandlerInContext({
		context,
		stepName: "investableUniverse",
		handleSubmit,
		baseSubmitFn: async (values) => {
			try {
				onStepDataChange(await saveHandlers.investableUniverse(values), {
					skipMetadataUpdate: true,
					persist: true,
				});
			} catch (err) {
				onStepError();
				reportPlatformError(err, "ERROR", "portfolio-creation", {
					message: "Save universe step",
					payload: JSON.stringify(values),
				});
				throw err;
			}
		},
		onInvalid(e) {
			console.log(e, getValues(), formState.dirtyFields);
		},
	});

	const initialInvestmentComposition = useQuery({
		...portfolioRepository.wizardInvestableUniverse.getQueryConfig(context.area, initialUniverseIdentifier!),
		enabled: initialUniverseIdentifier != null && initialUniverseType === "INSTRUMENTS_UNIVERSE",
	});

	useQueryReactor(initialInvestmentComposition, {
		onFetching() {
			if (initialInvestmentComposition.data) {
				setValue("universeComposition", initialInvestmentComposition.data);
			}
		},
		onSuccess(universeComposition) {
			setValue("universeComposition", universeComposition);
		},
	});

	return (
		<StepBase optional={stepMetadata.optional} title="Investable universe" onSubmitAsync={() => onSubmitAsync()}>
			<FormProvider {...form}>
				<InvestableUniverseHeader step={props} mode={mode} />
				{mode === "edit" && universeIdentifier == null && (
					<Banner severity="info" className="mb-4">
						If you have a specific universe in mind, you can{" "}
						<button
							data-qualifier="portfolioWizard/investableUniverse/uploadYourOwnUniverse"
							type="button"
							onClick={() => {
								const intentId = generateIntentId();
								trackMixPanelEvent("universe.entityActions.createIntent", {
									intentId,
								});
								push("Portfolios/ManualCreation", {
									entity: ManualCreationEntitiesEnum.Universe,
									intentId,
								});
							}}
							className="underline font-medium"
						>
							upload your own universe
						</button>{" "}
						in the “Universes” section of Portfolio Studio. Alternatively, you can{" "}
						<button type="button" className="font-bold">
							choose from our range of MDOTM universes
						</button>
						, which cover various asset classes, including indices, funds and ETFs, as well as single stocks. Select the
						option that aligns with your preferences and requirements.
					</Banner>
				)}

				<InvestableUniverseMainBlock step={props} />
			</FormProvider>
		</StepBase>
	);
}

const investableUniverseValidationObject = {
	universeIdentifier: z.string({
		invalid_type_error: "Please select a universe to continue",
	}),
	universeName: z.string().min(1, "Required field"),
	universeComposition: z.array(z.any()).optional(),
	universeGroupComposition: z
		.object({
			uuid: z.string().optional(),
			name: z.string().optional(),
			groups: z.array(z.any()).optional(),
		})
		.optional(),
};

const { universeName: _universeName, ...common } = investableUniverseValidationObject;
const commonInvestableUniverseValidation = z.object(common).and(universeTypeValidation());

function universeTypeValidation() {
	return z.discriminatedUnion("type", [
		z.object({
			type: z.literal(InvestableUniverseType.GroupUniverse),
			groupUniverseIds: z.array(z.string()).min(1, "You must at least select 1 group"),
		}),
		z.object({ type: z.literal(InvestableUniverseType.InstrumentsUniverse) }),
	]);
}

function InvestableUniverseHeader({ step, mode }: { step: StepPropsFor<"investableUniverse">; mode: "edit" | "read" }) {
	const { context, stepData } = step;
	const { area } = context;

	const { t } = useTranslation();
	const formCtx = useFormContext<InvestableUniverseStep>();
	const { watch, setValue } = formCtx;
	const universeIdentifier = watch("universeIdentifier");
	const groupUniverseIds = watch("groupUniverseIds");
	const type = watch("type");
	const user = useUserValue();
	const hasUniverseGroupPermission = useMemo(() => hasAccess(user, { requiredService: "GROUPS_UNIVERSE" }), [user]);

	const queryInvestableUniverseRaw = portfolioRepository.wizardInvestableUniverses.useQuery(context.area);

	const queryInvestableUniverse = useMappedQuery(
		(response) => {
			const availableUniverses = hasUniverseGroupPermission
				? response
				: response.filter((x) => x.type === "INSTRUMENTS_UNIVERSE");
			const options = availableUniverses.map((universe) => ({
				label: assocStatusToEntity(universe.universeName, universe.status) ?? "",
				value: {
					universeIdentifier: universe.universeIdentifier!,
					type: universe.type,
				},
				group:
					universe.universeIdentifier === CURRENT_COMPOSITION_ID
						? ""
						: hasUniverseGroupPermission
							? universe.type === InvestableUniverseType.GroupUniverse
								? "Universe with groups"
								: "Universe"
							: "Available universes",
				disabled: universe.available === false,
			}));

			return options.sort(builtInSortFnFor("group"));
		},
		[hasUniverseGroupPermission],
		queryInvestableUniverseRaw,
	);

	const querySingleGroupInstrumentsRaw = useQuery({
		...universeWithGroupsRepository.universeWithGroup.getQueryConfig(universeIdentifier!),
		enabled: type === InvestableUniverseType.GroupUniverse && universeIdentifier != null,
	});

	const querySingleGroupInstruments = useMappedQuery(
		(response) => {
			const groups = response?.groups ?? [];
			const options = groups?.map((x) => ({
				label: x.name!,
				value: x.uuid!,
			}));

			return options.sort(builtInSortFnFor("label"));
		},
		[],
		querySingleGroupInstrumentsRaw,
	);

	useQueryReactor(querySingleGroupInstrumentsRaw, {
		onSuccess(response) {
			setValue("universeGroupComposition", response);
		},
	});

	if ((area.name === "settings-current" || area.name === "settings-enhanced") && mode === "read") {
		if (stepData.universeName) {
			return (
				<Text type="Body/M/Book" as="p" className="mb-2">
					{stepData.universeName}
				</Text>
			);
		}

		return (
			<div className="h-full">
				<IconWalls.ConstraintNotSet constraintName="universe" />
			</div>
		);
	}

	return (
		<div
			className={`bg-[var(--global-palette-neutral-50)] z-0 relative -mx-4 px-4 py-4 mb-6 border-y border-[var(--global-palette-neutral-200)]`}
		>
			<ReactQueryWrapperBase query={queryInvestableUniverse}>
				{(universes) => (
					<div className="flex items-end space-x-2">
						<Controller value={false}>
							{({ value: isLoading, onChange: setIsLoading }) => (
								<FormField label={t("PORTFOLIOS.PORTFOLIO_UNIVERSE_PLACEHOLDER")} className="w-[413px]">
									{(fieldProps) => (
										<Select
											{...fieldProps}
											enableSearch
											value={{ type: watch("type"), universeIdentifier }}
											triggerDataAttrs={{
												"data-qualifier": "portfolioWizard/investableUniverse/universeIdentifier",
											}}
											data-qualifier="portfolioWizard/investableUniverse/loadUniverse"
											options={universes}
											disabled={mode === "read" || isLoading}
											onChange={(universeInfo) =>
												unpromisify(async () => {
													if (!universeInfo) {
														return;
													}

													if (universeInfo.type === "GROUP_UNIVERSE") {
														setValue("universeIdentifier", universeInfo?.universeIdentifier, setValueOptions);
														setValue("type", universeInfo?.type, setValueOptions);
														setValue("groupUniverseIds", [], setValueOptions);
														if (area.name !== "create") {
															setValue("investableUniverseSelectionStrategy", "SELL_INSTRUMENTS", setValueOptions);
														}
														return;
													}

													setIsLoading(true);
													const universeComposition = await portfolioRepository.wizardInvestableUniverse.fetchQuery(
														context.area,
														universeInfo.universeIdentifier!,
													);

													mergeOrSetUniverseInstruments(universeInfo.universeIdentifier!, universeComposition, {
														form: formCtx,
														step,
													});
													setIsLoading(false);
												})()
											}
										/>
									)}
								</FormField>
							)}
						</Controller>

						{type === InvestableUniverseType.GroupUniverse && (
							<ReactQueryWrapperBase query={querySingleGroupInstruments}>
								{(groupOptions) => (
									<FormField label="Choose which group to add" className="w-[413px]">
										{(fieldProps) => (
											<Select
												{...fieldProps}
												multi
												enableSearch
												value={groupUniverseIds ?? []}
												onChange={(newGroupIds) => setValue("groupUniverseIds", newGroupIds, setValueOptions)}
												options={groupOptions}
												multiSelectLabel={(selected) => `${selected} of ${groupOptions.length} selected`}
												triggerDataAttrs={{
													"data-qualifier": "portfolioWizard/investableUniverse/universeGroupsId",
												}}
												data-qualifier="portfolioWizard/investableUniverse/universeGroupsId/list"
												disabled={mode === "read"}
											/>
										)}
									</FormField>
								)}
							</ReactQueryWrapperBase>
						)}
					</div>
				)}
			</ReactQueryWrapperBase>
		</div>
	);
}

/** @internal — exported for tests */
export function mergeOrSetUniverseInstruments(
	universeIdentifier: string,
	universeComposition: PartialInvestableUniverseTicker[],
	ctx: {
		form: UseFormReturn<InvestableUniverseStep, any, InvestableUniverseStep>;
		step: StepPropsFor<"investableUniverse">;
	},
) {
	const { form, step } = ctx;
	const { setValue } = form;
	if (step.context.area.name === "create") {
		setValue("universeIdentifier", universeIdentifier, setValueOptions);
		setValue("type", "INSTRUMENTS_UNIVERSE", setValueOptions);
		setValue("groupUniverseIds", [], setValueOptions);
		setValue("universeComposition", universeComposition, setValueOptions);
		return;
	}

	const rawUniverse = universeComposition.filter((c) => c.inInvestmentComposition === false);
	const additionalInstruments = universeComposition.filter(isAdditional);

	if (additionalInstruments.length === 0 && rawUniverse.length === 0) {
		setValue("investableUniverseSelectionStrategy", "SELL_INSTRUMENTS", setValueOptions);
	}

	if (additionalInstruments.length > 0 || universeIdentifier === "CREATE_FROM_CURRENT_COMPOSITION") {
		const investableUniverseSelectionStrategy =
			universeIdentifier === "CREATE_FROM_CURRENT_COMPOSITION" ? "CREATE_NEW" : undefined;

		setValue("investableUniverseSelectionStrategy", investableUniverseSelectionStrategy);
		spawnUniverseMergeStrategyDialog({
			step,
			instruments: universeComposition,
			universeIdentifier,
			form,
			onSubmit(mergeForm) {
				setValue("universeName", mergeForm.name, setValueOptions);
				setValue("investableUniverseSelectionStrategy", mergeForm.investableUniverseSelectionStrategy, setValueOptions);
				setValue("universeIdentifier", universeIdentifier, setValueOptions);
				setValue("type", "INSTRUMENTS_UNIVERSE", setValueOptions);
				setValue("groupUniverseIds", [], setValueOptions);
				setValue("universeComposition", universeComposition, setValueOptions);
			},
		});
		return;
	}

	setValue("investableUniverseSelectionStrategy", "SELL_INSTRUMENTS", setValueOptions);
	setValue("universeComposition", universeComposition, setValueOptions);
	setValue("universeIdentifier", universeIdentifier, setValueOptions);
	setValue("type", "INSTRUMENTS_UNIVERSE", setValueOptions);
	setValue("groupUniverseIds", [], setValueOptions);
}

function InvestableUniverseMainBlock({ step }: { step: StepPropsFor<"investableUniverse"> }) {
	const form = useFormContext<InvestableUniverseStep>();
	const { watch } = form;
	const instruments = watch("universeComposition");
	const type = watch("type");

	if (type === "INSTRUMENTS_UNIVERSE" && instruments.length > 0) {
		return <UniverseInstrumentTable instruments={instruments} step={step} />;
	}

	if (type === InvestableUniverseType.GroupUniverse) {
		return <GroupedUniverseTable />;
	}

	return <></>;
}

function InstrumentTableProvider(props: {
	instruments: PartialInvestableUniverseTicker[];
	children: NodeOrFn<{
		userInstrumentsMap: Immutable.Map<string, UserInstrumentDto>;
		rowsWithUserInstrumentMetadata: InvestableUniverseInstrument[];
	}>;
}) {
	const { instruments } = props;
	const { watch } = useFormContext<InvestableUniverseStep>();

	/** check if portfolio goes in userInstruments */
	const strategy = watch("investableUniverseSelectionStrategy");
	const tickers = useMemo(
		() =>
			instruments
				.filter((x) => x.proxyOverwriteType !== "PORTFOLIO_MIXED" && x.ticker)
				.map((x) => x.ticker)
				.filter((ticker) => ticker != null),
		[instruments],
	);

	const queryUserInstruments = instrumentsRepository.userClassifications.useQuery(tickers);

	const queryUserInstrumentsMap = useMappedQuery(
		(userInstrumentsMap) => {
			const rowsWithUserInstrumentMetadata = instruments.map((instrument): InvestableUniverseInstrument => {
				const userInstrument = userInstrumentsMap.get(instrument.ticker!) ?? {};
				return {
					...userInstrument,
					name: userInstrument.name ?? instrument.instrument,
					alias: userInstrument.identifiers?.ALIAS ?? instrument.alias,
					additional: isAdditional(instrument),
					strategy,
					tickerName: userInstrument.tickerName ?? instrument.ticker,
				};
			});
			return { userInstrumentsMap, rowsWithUserInstrumentMetadata };
		},
		[instruments, strategy],
		queryUserInstruments,
	);

	return (
		<ReactQueryWrapperBase query={queryUserInstrumentsMap}>
			{(response) => renderNodeOrFn(props.children, response)}
		</ReactQueryWrapperBase>
	);
}

function UniverseInstrumentTable({
	instruments,
	step,
}: {
	step: StepPropsFor<"investableUniverse">;
	instruments: PartialInvestableUniverseTicker[];
}) {
	const { area } = step.context;
	const columns = columnsDefinition.useInstrumentInvestableUniverse(area.name);
	const persistColsProps = agExtra.usePersistColState(
		area.name === "create" || area.name === "draft"
			? persistedColStateKeys["instrument-investableUniverse"]
			: persistedColStateKeys["instrument-investableUniverse-withStatus"],
	);

	return (
		<InstrumentTableProvider instruments={instruments}>
			{({ rowsWithUserInstrumentMetadata }) => (
				<>
					<Text type="Body/M/Bold" className="mb-4">
						Universe preview
					</Text>
					<p className="mb-4">To create or edit tags open the universe in the universes section and click edit.</p>
					<Searchable extractFn={(item) => Object.values(item).join(" ")} collection={rowsWithUserInstrumentMetadata}>
						{({ query, setQuery, filtered }) => {
							return (
								<>
									<div className="mb-4">
										<TextInput
											data-qualifier="portfolioWizard/investableUniverse/instrumentsQuery"
											placeholder="Search by instruments name, identifier o asset class"
											value={query}
											onChangeText={setQuery}
											style={{ width: 692 }}
											size="small"
											leftContent={<Icon icon="Search" />}
										/>
									</div>
									<AgGridWithModules<InvestableUniverseInstrument>
										{...persistColsProps}
										columnDefs={columns}
										rowData={filtered}
										getRowId={(x) => x.data.tickerName!}
									/>
								</>
							);
						}}
					</Searchable>
				</>
			)}
		</InstrumentTableProvider>
	);
}

function GroupedUniverseTable() {
	const { watch } = useFormContext<InvestableUniverseStep>();
	const groupUniverseIds = watch("groupUniverseIds");
	const groupUniverseIdsSet = useMemo(() => new Set(groupUniverseIds ?? []), [groupUniverseIds]);
	const universeGroupComposition = watch("universeGroupComposition");

	const selectedGroups = useMemo(() => {
		const groups = universeGroupComposition?.groups ?? [];

		return groups.filter((group) => groupUniverseIdsSet.has(group.uuid!));
	}, [universeGroupComposition?.groups, groupUniverseIdsSet]);

	return <UniverseWithGroupsCardContent mode="read" variant="selection" groups={selectedGroups} />;
}

type MergeStrategyForm = {
	name?: string;
	investableUniverseSelectionStrategy: InvestableUniverseSelectionStrategy;
};

type UniverseMergeStrategyDialogProps = MinimumDialogProps & SpawnUniverseMergeStrategyDialogProps;
type SpawnUniverseMergeStrategyDialogProps = {
	onSubmit(form: MergeStrategyForm): void;
	step: StepPropsFor<"investableUniverse">;
	instruments: PartialInvestableUniverseTicker[];
	universeIdentifier?: string;
	form: UseFormReturn<InvestableUniverseStep>;
};

/** exported for 02-InvestableUniverse.test.tsx */
export function UniverseMergeStrategyDialog({
	step,
	show,
	onClose,
	onSubmit,
	instruments,
	universeIdentifier,
	form,
	onAnimationStateChange,
}: UniverseMergeStrategyDialogProps) {
	const { context } = step;
	const { name } = context.area;

	const { t } = useTranslation();
	const { getValues } = form;
	const investableUniverseSelectionStrategyStep = getValues("investableUniverseSelectionStrategy");

	const { checkIfNameIsAvailable, checkingNameUniqueness } = useDebouncedNameUniquenessChecker({
		isNameAvailableApi: universeRepository.isUniverseNameAvailable,
		currentName: null,
	});

	const mergeStrategyForm = useForm<{
		name: string | null;
		investableUniverseSelectionStrategy: InvestableUniverseSelectionStrategy | null;
	}>({
		defaultValues: {
			name: null,
			investableUniverseSelectionStrategy: investableUniverseSelectionStrategyStep || null,
		},
		resolver: zodResolver(
			z.discriminatedUnion("investableUniverseSelectionStrategy", [
				z.object({
					investableUniverseSelectionStrategy: z.literal("CREATE_NEW"),
					name: z
						.string({
							invalid_type_error: "Please provide a name for your universe",
							required_error: "Please provide a name for your universe",
						})
						.min(1, "Please provide a name for your universe")
						.refine(checkIfNameIsAvailable, {
							message: t("MISSING_FIELD.NAME_NOT_AVAILABLE"),
						}),
				}),
				z.object({ investableUniverseSelectionStrategy: z.literal("EXPAND") }),
				z.object({
					investableUniverseSelectionStrategy: z.literal("SELL_INSTRUMENTS"),
				}),
			]),
		),
	});

	const internalInvestableUniverseSelectionStrategy = mergeStrategyForm.watch("investableUniverseSelectionStrategy");

	const isCreateFromCurrentComposition = universeIdentifier === CURRENT_COMPOSITION_ID;
	/** what the selected universe is missing: the rows "expand" would add and "sell instruments" would liquidate */
	const additionalInstruments = useMemo(() => instruments.filter(isAdditional), [instruments]);
	// Creating a universe from the composition has nothing to diff against: the whole composition becomes the universe.
	const tableInstruments = isCreateFromCurrentComposition ? instruments : additionalInstruments;

	return (
		<Dialog
			show={show}
			size="xxlarge"
			onAnimationStateChange={onAnimationStateChange}
			onSubmitAsync={mergeStrategyForm.handleSubmit((formValues) => {
				onSubmit(formValues as MergeStrategyForm); // FIX-ME: forcing types
			})}
			onClose={onClose}
			header={
				<DialogHeader
					data-qualifier="portfolioWizard/investableUniverse/universeDiff/title"
					icon={
						isCreateFromCurrentComposition ? <></> : <Icon icon="Icon-full-alert" color={colorBySeverity.warning} />
					}
				>
					{isCreateFromCurrentComposition
						? "Create a new universe from the portfolio composition"
						: additionalInstruments.length === 1
							? "One instrument is not included in the selected universe."
							: "Some instruments are not included in the selected universe."}
				</DialogHeader>
			}
			footer={
				<DialogFooter
					primaryAction={
						<SubmitButton data-qualifier="portfolioWizard/investableUniverse/universeDiff/confirm">
							{isCreateFromCurrentComposition ? "Create universe" : t("BUTTON.CONFIRM")}
						</SubmitButton>
					}
					neutralAction={
						<Button
							onClick={onClose}
							palette="tertiary"
							data-qualifier="portfolioWizard/investableUniverse/universeDiff/cancel"
						>
							{t("BUTTON.CANCEL")}
						</Button>
					}
				/>
			}
		>
			<div className="flex flex-col gap-4">
				{isCreateFromCurrentComposition ? (
					<div data-qualifier="portfolioWizard/investableUniverse/universeDiff/hint">
						To ensure accurate portfolio management, all portfolio instruments must be contained within a universe. If
						you didn&apos;t find an appropriate existing universe to connect this portfolio, you can create one from it.{" "}
					</div>
				) : (
					<div data-qualifier="portfolioWizard/investableUniverse/universeDiff/warning">
						{additionalInstruments.length === 1
							? "Sphere has detected that 1 instrument in your portfolio is not included in the selected universe."
							: `Sphere has detected that ${additionalInstruments.length} instruments in your portfolio are not included in the selected universe.`}{" "}
						To ensure accurate portfolio management, all portfolio instruments must be contained within the universe.
					</div>
				)}

				{internalInvestableUniverseSelectionStrategy === "CREATE_NEW" && (
					<FormFields.Text
						name="name"
						control={mergeStrategyForm.control}
						formState={mergeStrategyForm.formState}
						label="Universe name"
						placeholder="Insert universe name..."
						rightContent={
							checkingNameUniqueness ? <CircularProgressBar className="w-3" value="indeterminate" /> : undefined
						}
						className={{
							"order-5": !isCreateFromCurrentComposition,
							"w-[448px]": true,
						}}
						data-qualifier="portfolioWizard/investableUniverse/universeDiff/name"
					/>
				)}

				<InstrumentTableProvider instruments={tableInstruments}>
					{({ rowsWithUserInstrumentMetadata }) => (
						<div className="flex flex-col min-h-0 h-[400px]">
							<AgGridWithModules<InvestableUniverseInstrument>
								columnDefs={universeMergeStrategyColumns}
								rowData={rowsWithUserInstrumentMetadata}
								getRowId={(x) => x.data.tickerName!}
								autoSizeStrategy={{ type: "fitGridWidth" }}
							/>
						</div>
					)}
				</InstrumentTableProvider>

				{name !== "draft" && !isCreateFromCurrentComposition && (
					<>
						<div>You have a few options to address this issue:</div>
						<FormController
							control={mergeStrategyForm.control}
							name="investableUniverseSelectionStrategy"
							render={({ field: { value, onChange }, fieldState }) => (
								<>
									<RadioGroup value={value} onChange={onChange}>
										<div
											data-qualifier="portfolioWizard/investableUniverse/universeDiff/strategy"
											className="flex flex-row flex-wrap gap-4"
										>
											<Radio
												data-qualifier="portfolioWizard/investableUniverse/universeDiff/strategy(expand)"
												value={InvestableUniverseSelectionStrategy.Expand}
											>
												Expand selected universe
											</Radio>
											{context.area.name !== "settings-current" && (
												<Radio
													data-qualifier="portfolioWizard/investableUniverse/universeDiff/strategy(createNew)"
													value={InvestableUniverseSelectionStrategy.CreateNew}
												>
													Create new universe
												</Radio>
											)}
											<Radio
												data-qualifier="portfolioWizard/investableUniverse/universeDiff/strategy(sellInstruments)"
												value={InvestableUniverseSelectionStrategy.SellInstruments}
											>
												Sell instruments
											</Radio>
										</div>
									</RadioGroup>
									{fieldState.error?.message && (
										<div className="mt-4">
											<Banner data-qualifier="portfolioWizard/investableUniverse/universeDiff/error" severity="error">
												{fieldState.error?.message}
											</Banner>
										</div>
									)}
								</>
							)}
						/>
					</>
				)}
			</div>
		</Dialog>
	);
}

const universeMergeStrategyColumns: ColDef<InvestableUniverseInstrument>[] = [
	sphereColumnsDef.string("name"),
	sphereColumnsDef.string("alias", { headerName: "Alias" }),
];

function spawnUniverseMergeStrategyDialog(params: SpawnUniverseMergeStrategyDialogProps) {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ resolve, show, onHidden }) => (
			<FormProvider {...params.form}>
				<UniverseMergeStrategyDialog
					{...params}
					show={show}
					onClose={() => resolve()}
					onSubmit={(x) => {
						params.onSubmit(x);
						resolve();
					}}
					onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				/>
			</FormProvider>
		)),
	);
}

/**
 * verify is the given instrument exist in the composition but not in the universe
 */
function isAdditional(instrument: PartialInvestableUniverseTicker): boolean {
	return Boolean(!instrument.inUniverseComposition && instrument.inInvestmentComposition);
}
